import { useMemo, useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Paper,
  Collapse,
  Chip,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import {
  ArrowForwardIos,
  Edit,
  Visibility,
  Warning,
} from "@mui/icons-material";

const DuplicatedAlertList = ({ items, entityName }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);

  const safeItems = items || [];

  const entityLabel = entityName?.toLowerCase() || "registro";
  const pluralEntityLabel = `${entityLabel}s`;

  const headingText = useMemo(() => {
    const count = safeItems.length;
    return `${count} ${count === 1 ? entityLabel : pluralEntityLabel} encontrados`;
  }, [safeItems.length, entityLabel, pluralEntityLabel]);

  if (!safeItems.length) return null;

  const getFullName = (item = {}) => {
    const name = String(item?.name || "").trim();
    const surname = String(item?.surname || "").trim();
    const fullName = `${name} ${surname}`.trim();
    return fullName || "--";
  };

  const getLawyerName = (item = {}) => {
    const lawyerName = String(item?.lawyers_relation?.name || "").trim();
    const lawyerSurname = String(item?.lawyers_relation?.surname || "").trim();
    const fullLawyerName = `${lawyerName} ${lawyerSurname}`.trim();
    return fullLawyerName || "--";
  };

  const getCompanyName = (item = {}) =>
    String(item?.company_relation?.company_name || "").trim() || "--";

  const renderDetailField = (label, value) => (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Typography component="span" sx={{ fontWeight: 700 }}>
        {label}:
      </Typography>
      <Typography component="span">{value || "--"}</Typography>
    </Box>
  );

  const handleToggleView = (id) => {
    setExpandedItemId((prevId) => (prevId === id ? null : id));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setExpandedItemId(null);
  };

  const cardGradient =
    "linear-gradient(120deg, rgba(255, 193, 7, 0.10) 0%, rgba(255, 255, 255, 0.96) 40%, rgba(255, 255, 255, 1) 100%)";

  return (
    <Grid item xs={6}>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: "12px",
          background: cardGradient,
          borderColor: "rgba(245, 124, 0, 0.32)",
          boxShadow: "0px 6px 22px -18px rgba(0,0,0,0.35)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(245, 124, 0, 0.16)",
              }}
            >
              <Warning color="warning" fontSize="small" />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Detección de coincidencias
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Revise posibles duplicados antes de continuar
              </Typography>
            </Box>
          </Box>

          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={headingText}
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Divider sx={{ my: 1.25, borderColor: "rgba(0,0,0,0.08)" }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            La lista completa se muestra en una ventana dedicada.
          </Typography>

          <Button
            color="warning"
            variant="contained"
            onClick={() => setOpenDialog(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Ver coincidencias
          </Button>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {headingText.charAt(0).toUpperCase() + headingText.slice(1)}
              </Typography>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={pluralEntityLabel}
              />
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={1.5}>
              {safeItems.map((item, index) => {
                const itemId = item?.id ?? index;
                const isExpanded = expandedItemId === itemId;

                return (
                  <Paper
                    key={itemId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      borderColor: isExpanded
                        ? "rgba(25, 118, 210, 0.35)"
                        : "rgba(0,0,0,0.12)",
                      bgcolor: isExpanded
                        ? "rgba(25, 118, 210, 0.03)"
                        : "rgba(255,255,255,0.9)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {getFullName(item)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item?.match_type
                            ? `Coincidencia por ${item.match_type}`
                            : "Coincidencia detectada"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<Visibility />}
                          onClick={() => handleToggleView(itemId)}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<Edit />}
                          component={NavLink}
                          to={`/${entityName}s/editar?id=${item?.id}`}
                        >
                          Editar
                        </Button>
                      </Box>
                    </Box>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.75}>
                        {renderDetailField("Nombre", getFullName(item))}
                        {renderDetailField(
                          "Documento (NIF)",
                          item?.nif || "--",
                        )}
                        {renderDetailField("Teléfono", item?.phone || "--")}
                        {renderDetailField(
                          "Correo electrónico",
                          item?.email || "--",
                        )}
                        {renderDetailField("Abogado", getLawyerName(item))}
                        {renderDetailField("Empresa", getCompanyName(item))}
                      </Stack>

                      <Box
                        sx={{
                          mt: 1.5,
                          justifyContent: "flex-end",
                          display: "flex",
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          endIcon={<ArrowForwardIos />}
                          component={NavLink}
                          to={`/${entityName}s/ver?id=${item?.id}`}
                        >
                          Ir al {entityLabel}
                        </Button>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              color="error"
              variant="outlined"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Grid>
  );
};

export default DuplicatedAlertList;
